import { ProductInfoRating } from '@/app/[locale]/product/[slug]/_components/product-info/product-info-rating'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { SearchIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

type Props = {
  onFilterChange?: (value: string) => void
  onSearchChange?: (value: string) => void
}

export const ProductReviewsFilterDesktop = ({
  onFilterChange,
  onSearchChange,
}: Props) => {
  const [searchValue, setSearchValue] = useState('')
  const [debouncedKeyword] = useDebounceValue(searchValue, 500)
  const [currentFilter, setCurrentFilter] = useState('')
  const [currentSort, setCurrentSort] = useState('')
  const t = useTranslations('product_detail.title')

  // Gọi onSearchChange khi debouncedKeyword thay đổi
  useEffect(() => {
    if (debouncedKeyword && onSearchChange) {
      onSearchChange(debouncedKeyword)
    }
  }, [debouncedKeyword, onSearchChange])

  // Cập nhật filter khi filter hoặc sort thay đổi
  useEffect(() => {
    if (onFilterChange) {
      // Kết hợp filter và sort, loại bỏ giá trị rỗng
      const filters = [currentFilter, currentSort].filter(Boolean)
      if (filters.length > 0) {
        onFilterChange(filters.join(','))
      }
    }
  }, [currentFilter, currentSort, onFilterChange])

  const sortOptions = [
    {
      value: 'star_order_by:desc',
      label: (
        <span>
          {t('rating')}: {t('rating_desc')}
        </span>
      ),
    },
    {
      value: 'star_order_by:asc',
      label: (
        <span>
          {t('rating')}: {t('rating_asc')}
        </span>
      ),
    },
  ]

  const handleFilterRating = (rating: number | null) => {
    if (rating === null) {
      setCurrentFilter('')
    } else {
      setCurrentFilter(`star:${rating}`)
    }
  }

  const handleFilterWithImage = (value: boolean) => {
    setCurrentFilter(`has_picture_attached:${value}`)
  }

  const handleSortChange = (value: string) => {
    setCurrentSort(value)
  }

  const handleFilterHasResponse = (value: boolean) => {
    setCurrentFilter(`has_feedback:${value}`)
  }

  return (
    <div>
      <div className='relative'>
        <Input
          type='search'
          placeholder={t('search_review')}
          label={t('filter_review')}
          className='h-11 rounded-full text-sm placeholder:text-neutral-800/50'
          prefix={<SearchIcon />}
          wrapperClassName='[&_label]:font-medium [&_label]:text-neutral-900/40 [&_label]:ml-0'
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </div>

      <div className='mt-5 md:hidden'>
        <p className='mb-3 font-sans text-sm font-medium leading-4.5 text-neutral-900/40'>
          {t('sort_by_follow')}
        </p>
        <Select
          placeholder={t('sort_by')}
          className={{
            trigger:
              'w-full data-[placeholder]:font-normal data-[placeholder]:text-neutral-500',
          }}
          options={sortOptions}
          onValueChange={handleSortChange}
        />
      </div>

      <div className='mt-8 flex items-center gap-3'>
        <RatingClassification
          onFilterRating={handleFilterRating}
          onFilterWithImage={handleFilterWithImage}
          onFilterHasResponse={handleFilterHasResponse}
        />
      </div>
    </div>
  )
}

type RatingClassificationProps = {
  onFilterRating?: (rating: number | null) => void
  onFilterWithImage?: (value: boolean) => void
  onFilterHasResponse?: (value: boolean) => void
}

const RatingClassification = ({
  onFilterRating,
  onFilterWithImage,
  onFilterHasResponse,
}: RatingClassificationProps) => {
  const t = useTranslations('product_detail.title')
  const [hasResponseChecked, setHasResponseChecked] = useState(false)
  const [withImageChecked, setWithImageChecked] = useState(false)
  const [selectedRate, setSelectedRate] = useState<number | null>(null)

  const rates = [
    { rate: 5, count: 99 },
    { rate: 4, count: 99 },
    { rate: 3, count: 99 },
    { rate: 2, count: 99 },
    { rate: 1, count: 99 },
  ]

  const handleRatingChange = (rate: number) => {
    const newSelectedRate = selectedRate === rate ? 5 : rate
    setSelectedRate(newSelectedRate)
    onFilterRating?.(newSelectedRate)
  }

  return (
    <div>
      <div>
        <p className='mb-3 font-sans text-sm font-medium leading-4.5 text-neutral-900/40'>
          {t('rating_classification')}
        </p>
        <ul className='space-y-6'>
          {rates.map((item) => (
            <li key={item.rate}>
              <Checkbox
                title={
                  <div className='flex items-center gap-2 selection:bg-transparent'>
                    <p className='font-sans text-base leading-5 text-neutral-700'>
                      {item.rate}
                    </p>
                    <ProductInfoRating
                      rating={{ avg: item.rate, count: item.count }}
                      showText={false}
                      className='gap-1 [&_img]:h-4 [&_img]:w-4'
                      showAvgText={false}
                    />
                  </div>
                }
                checked={selectedRate === item.rate}
                onCheckedChange={() => handleRatingChange(item.rate)}
              />
            </li>
          ))}
        </ul>
      </div>
      <div className='mt-6 flex items-center px-2 pt-1.5'>
        <Image
          src={ImageHelper.getImageUrl(
            '//image/February2025/mceclip0_64.png',
            '',
            52
          )}
          alt='icon check'
          width={26}
          height={26}
          unoptimized
        />
        <p className='ml-0.5 font-sans text-xs text-primary'>
          {t('rating_classification_description')}
        </p>
      </div>
      <div>
        <p className='mb-3 font-sans text-sm font-medium leading-4.5 text-neutral-900/40'>
          {t('filter_response')}
        </p>
        <ul className='space-y-3'>
          <li>
            <Checkbox
              title={
                <div className='flex items-center gap-2 selection:bg-transparent'>
                  <p className='font-sans text-base leading-5 text-neutral-700'>
                    {t('has_response')}
                  </p>
                </div>
              }
              checked={hasResponseChecked}
              onCheckedChange={(checked) => {
                const isChecked = checked as boolean
                setHasResponseChecked(isChecked)
                if (isChecked && withImageChecked) {
                  setWithImageChecked(false)
                  onFilterWithImage?.(false)
                }
                onFilterHasResponse?.(isChecked)
              }}
            />
          </li>
          <li>
            <Checkbox
              title={
                <div className='flex items-center gap-2 selection:bg-transparent'>
                  <p className='font-sans text-base leading-5 text-neutral-700'>
                    {t('has_image')}
                  </p>
                  {/* <p className='font-sans text-sm leading-4.5 text-neutral-500'>
                    ( 15 )
                  </p> */}
                </div>
              }
              checked={withImageChecked}
              onCheckedChange={(checked) => {
                const isChecked = checked as boolean
                setWithImageChecked(isChecked)
                if (isChecked && hasResponseChecked) {
                  setHasResponseChecked(false)
                  onFilterHasResponse?.(false)
                }
                onFilterWithImage?.(isChecked)
              }}
            />
          </li>
        </ul>
      </div>
    </div>
  )
}
